import {
  KeywordPromptsDTO,
  SlackAppVersion,
  SlackConnectionDTO,
  TeamsConnectionDTO,
} from "groopit-js";
import * as React from "react";
import * as ReactDOM from "react-dom";
import {
  groupByConnection,
  SlackConnectionLinks,
  TeamsConnectionLinks,
} from "./GroupedConnectionLinks";

interface KeywordPromptsActiveChannelChipProps {
  showLogo: boolean;
  connection: TeamsConnectionDTO | SlackConnectionDTO;
  label: string;
}

const KeywordPromptActiveChannelChip = (
  props: KeywordPromptsActiveChannelChipProps,
): JSX.Element => {
  return (
    <span className="chip">
      {props.showLogo && (
        <>
          {props.connection instanceof SlackConnectionDTO ? (
            <img
              className="filter-app-white h-full py-2"
              src="/Content/images/ic_slack.png"
            />
          ) : (
            <img
              className="filter-app-white h-full py-2"
              src="/Content/images/ic_teams.png"
            />
          )}
        </>
      )}
      <span>{props.label}</span>
    </span>
  );
};

interface KeywordPromptsActiveChannelsDisplayProps {
  connectionLinks: (TeamsConnectionLinks | SlackConnectionLinks)[];
  isConnectedToOutlook: boolean;
  excludedChannels: { [key: string]: string[] };
  hasMultipleConnections: boolean;
}

export const KeywordPromptsActiveChannelsDisplay = (
  props: KeywordPromptsActiveChannelsDisplayProps,
): JSX.Element => {
  const getOrgWideChannelCount = (
    connection: TeamsConnectionLinks | SlackConnectionLinks,
  ) => {
    if (connection instanceof TeamsConnectionLinks) {
      return connection.connection.botChannels.filter(
        (c) =>
          !(
            props.excludedChannels[connection.connection.id]?.includes(
              c.channelId,
            ) ?? false
          ),
      ).length;
    } else {
      return connection.connection.botChannels.filter(
        (c) =>
          !(
            props.excludedChannels[connection.connection.id]?.includes(c.id) ??
            false
          ),
      ).length;
    }
  };

  const getOrgWideChannelLabel = (
    connection: TeamsConnectionLinks | SlackConnectionLinks,
  ) => {
    if (
      connection instanceof SlackConnectionLinks &&
      connection.connection.version < SlackAppVersion.KeywordPromptsV2
    ) {
      return "All channels";
    } else {
      const count = getOrgWideChannelCount(connection);
      return `${count} ${count == 1 ? "channel" : "channels"}`;
    }
  };

  return (
    <div className="flex gap-2">
      {props.isConnectedToOutlook || props.connectionLinks.length > 0 ? (
        <>
          <p className="flex-shrink-0">Keyword prompts are active in</p>
          <div className="flex flex-wrap gap-2">
            {props.isConnectedToOutlook && (
              <span className="chip">
                <img
                  className="filter-app-white h-full py-2"
                  src="/Content/images/ic_outlook.png"
                />
                <span>Outlook</span>
              </span>
            )}
            {props.connectionLinks.map((connection) => (
              <div
                className="flex flex-wrap gap-2"
                key={connection.connection.id}
              >
                {connection.orgWideLink ? (
                  <KeywordPromptActiveChannelChip
                    showLogo={props.hasMultipleConnections}
                    connection={connection.connection}
                    label={getOrgWideChannelLabel(connection)}
                  />
                ) : (
                  connection.channelLinks.map((channel) => (
                    <KeywordPromptActiveChannelChip
                      key={channel.id}
                      showLogo={props.hasMultipleConnections}
                      connection={connection.connection}
                      label={channel.channelName ?? ""}
                    />
                  ))
                )}
                {props.hasMultipleConnections && (
                  <>
                    <span> in </span>
                    <span className="chip deactivated">
                      {connection.connection instanceof SlackConnectionDTO ? (
                        <img
                          className="filter-app-white h-full py-2"
                          src="/Content/images/ic_slack.png"
                        />
                      ) : (
                        <img
                          className="filter-app-white h-full py-2"
                          src="/Content/images/ic_teams.png"
                        />
                      )}
                      {connection.connection.name}
                    </span>
                  </>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="flex-shrink-0 italic">
          Keyword prompts are not active in any locations.
        </p>
      )}
    </div>
  );
};

document
  .querySelectorAll<HTMLElement>(".keyword-prompts-active-channels")
  .forEach((keywordPromptsActiveChannels) => {
    const keywordPrompts = KeywordPromptsDTO.fromJS(
      JSON.parse(keywordPromptsActiveChannels.dataset.keywordPrompts as string),
    );
    ReactDOM.render(
      <KeywordPromptsActiveChannelsDisplay
        connectionLinks={[
          ...groupByConnection(keywordPrompts.slackChannelLinks),
          ...groupByConnection(keywordPrompts.teamsChannelLinks),
        ]}
        excludedChannels={keywordPrompts.excludedChannels}
        isConnectedToOutlook={keywordPrompts.isOutlookLinked}
        hasMultipleConnections={true}
      />,
      keywordPromptsActiveChannels,
    );
  });
